import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Settings, Check, X, Globe } from 'lucide-react';

const TranslationSettings = () => {
  const { t, i18n } = useTranslation();
  const [selectedTranslation, setSelectedTranslation] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem(`selectedTranslation_${i18n.language}`);
    if (saved) {
      setSelectedTranslation(saved);
    } else {
      const defaultTranslation = i18n.language === 'fr' ? 'aelf' : 'kjv';
      setSelectedTranslation(defaultTranslation);
    }
  }, [i18n.language]);

  const handleTranslationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTranslation(e.target.value);
    setError(null);
  };

  const handleSave = () => {
    try {
      if (!selectedTranslation) {
        setError(t('settings.error'));
        return;
      }

      localStorage.setItem(`selectedTranslation_${i18n.language}`, selectedTranslation);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError(t('settings.saveError'));
    }
  };

  const handleReset = () => {
    localStorage.removeItem(`selectedTranslation_${i18n.language}`);
    const defaultTranslation = i18n.language === 'fr' ? 'aelf' : 'kjv';
    setSelectedTranslation(defaultTranslation);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  const translations = Object.keys(t('translations', { returnObjects: true }));

  return (
    <>
    <Helmet>
      <title>{t('settings.title')} — Bible4Me</title>
      <meta name="robots" content="noindex, follow" />
    </Helmet>
    <Card className="w-full max-w-lg mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-900" />
            <CardTitle className="text-amber-900">{t('settings.title')}</CardTitle>
          </div>
          <button
            onClick={toggleLanguage}
            className="p-2 hover:bg-amber-100 rounded-full transition-colors"
            aria-label="Toggle language"
          >
            <Globe className="w-5 h-5 text-amber-900" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-amber-900 block">
            {t('settings.select')}
          </label>
          <select
            value={selectedTranslation}
            onChange={handleTranslationChange}
            className="w-full p-2 border rounded-md border-amber-200 bg-white"
            aria-label={t('settings.placeholder') || ''}
          >
            <option value="">{t('settings.placeholder')}</option>
            {translations.map((code) => (
              <option key={code} value={code}>
                {t(`translations.${code}`)}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {showSuccess && (
          <Alert className="bg-green-50 border-green-200">
            <Check className="w-4 h-4 text-green-600" />
            <AlertDescription className="text-green-600">
              {t('settings.success')}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSave}
            className="flex-1 bg-amber-900 text-white px-4 py-2 rounded-md hover:bg-amber-800 transition-colors flex items-center justify-center gap-2"
            aria-label={t('settings.save') || 'Save'}
          >
            <Check className="w-4 h-4" />
            {t('settings.save')}
          </button>
          <button
            onClick={handleReset}
            className="flex-1 bg-amber-100 text-amber-900 px-4 py-2 rounded-md hover:bg-amber-200 transition-colors flex items-center justify-center gap-2"
            aria-label={t('settings.reset') || 'Reset'}
          >
            <X className="w-4 h-4" />
            {t('settings.reset')}
          </button>
        </div>
      </CardContent>
    </Card>
    </>
  );
};

export default TranslationSettings;